"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { CalendarPlus, Check, Clock, Hourglass, MapPin, Settings2, Trash2, Undo2, Users, X } from "lucide-react";
import { BookingSummary } from "@/lib/types/cal";
import { ROLE_BADGE } from "@/lib/constant";
import { modalRow } from "@/lib/animation/variant";
import { useModalOverlay } from "@/hooks/ui/useModalOverlay";
import { useCancelBooking } from "@/hooks/scheduling/useCancelBooking";
import { cityFromZone, dateIn, googleCalendarUrl, initialsOf, relativeToNow, timeIn } from "@/lib/utils/cal";
import AppButton from "../../shared/AppButton";
import InfoTileCard from "./InfoTileCard";
interface BookingSuccessCardProps {
  booking: BookingSummary | null;
  viewerZone: string | null;
  onDismiss: () => void;
  onReschedule: (uid: string) => void;
  /** Sends the visitor back to the inline booker after a cancellation. */
  onBookAgain?: () => void;
}

// Which face of the card is showing. The booking, the cancel form and the
// "it's gone" confirmation are one panel rather than three modals — closing and
// reopening a dialog between two halves of the same decision reads as a reset.
type PanelView = "confirmed" | "cancelling" | "cancelled";
interface BookingPanelProps extends Omit<BookingSuccessCardProps, "booking"> {
  booking: BookingSummary;
}
const BookingPanel = t0 => {
  const $ = _c(84);
  const {
    booking,
    viewerZone,
    onDismiss,
    onReschedule,
    onBookAgain
  } = t0;
  const [view, setView] = useState("confirmed");
  const [reason, setReason] = useState("");
  const {
    pending,
    error,
    cancel
  } = useCancelBooking();
  const zone = viewerZone ?? "UTC";
  const start = booking.start;
  const duration = booking.duration;
  const participants = booking.participants;
  const rescheduled = booking.rescheduled;
  const cancelled = view === "cancelled";
  let t1;
  if ($[0] !== start || $[1] !== zone) {
    t1 = start ? `${dateIn(zone, start)} · ${timeIn(zone, start)}` : null;
    $[0] = start;
    $[1] = zone;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== start || $[4] !== zone) {
    t2 = start ? cityFromZone(zone) : null;
    $[3] = start;
    $[4] = zone;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t1 || $[7] !== t2) {
    t3 = {
      icon: Clock,
      label: "When",
      value: t1,
      sub: t2
    };
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== start) {
    t4 = start ? relativeToNow(start, new Date()) : null;
    $[9] = start;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t4) {
    t5 = {
      icon: Hourglass,
      label: "Starts",
      value: t4,
      sub: null
    };
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const t6 = duration ? `${duration} minutes` : null;
  let t7;
  if ($[13] !== t6) {
    t7 = {
      icon: Clock,
      label: "How long",
      value: t6,
      sub: null
    };
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== booking.location) {
    t8 = {
      icon: MapPin,
      label: "Where",
      value: booking.location,
      sub: null
    };
    $[15] = booking.location;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t3 || $[18] !== t5 || $[19] !== t7 || $[20] !== t8) {
    t9 = [t3, t5, t7, t8].filter(_temp);
    $[17] = t3;
    $[18] = t5;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const facts = t9;
  let t10;
  if ($[22] !== booking.uid || $[23] !== cancel || $[24] !== reason) {
    t10 = async () => {
      if (!booking.uid) {
        return;
      }
      const done = await cancel(booking.uid, reason);
      if (done) {
        setView("cancelled");
      }
    };
    $[22] = booking.uid;
    $[23] = cancel;
    $[24] = reason;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  const handleCancel = t10;
  const headline = cancelled ? "IT\u2019S CANCELLED." : rescheduled ? "YOU\u2019RE MOVED." : "YOU\u2019RE BOOKED.";
  const blurb = cancelled ? "The slot is back on the calendar and everyone has been told. No hard feelings \u2014 book again whenever the timing works." : rescheduled ? "The old slot is released and an updated invite is on its way to your inbox." : "A calendar invite is on its way to your inbox. Bring the messy version of the idea \u2014 that\u2019s the useful one.";
  let t11;
  let t12;
  let t13;
  let t14;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      opacity: 0,
      y: 40,
      scale: 0.94
    };
    t12 = {
      opacity: 1,
      y: 0,
      scale: 1
    };
    t13 = {
      opacity: 0,
      y: 20,
      scale: 0.97
    };
    t14 = {
      type: "spring",
      stiffness: 260,
      damping: 26
    };
    $[26] = t11;
    $[27] = t12;
    $[28] = t13;
    $[29] = t14;
  } else {
    t11 = $[26];
    t12 = $[27];
    t13 = $[28];
    t14 = $[29];
  }
  const t15 = cancelled ? "radial-gradient(320px circle at 50% 0%, rgba(229,72,77,0.18), transparent 70%)" : "radial-gradient(320px circle at 50% 0%, rgba(178,209,39,0.18), transparent 70%)";
  let t16;
  if ($[30] !== t15) {
    t16 = <div aria-hidden={true} className="pointer-events-none absolute inset-x-0 top-0 h-48" style={{
      background: t15
    }} />;
    $[30] = t15;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <X size={16} />;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== onDismiss) {
    t18 = <button type="button" onClick={onDismiss} aria-label="Close" className="absolute right-4 top-4 z-10 grid size-9 cursor-pointer place-items-center rounded-full border border-white/15 text-white/60 transition-colors hover:border-primary hover:bg-primary hover:text-black">{t17}</button>;
    $[33] = onDismiss;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  const t19 = `absolute inset-0 rounded-full ${cancelled ? "bg-[#e5484d]/30" : "bg-primary/30"}`;
  let t20;
  let t21;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = {
      scale: [1, 1.6],
      opacity: [0.6, 0]
    };
    t21 = {
      duration: 1.8,
      repeat: Infinity,
      ease: "easeOut"
    };
    $[35] = t20;
    $[36] = t21;
  } else {
    t20 = $[35];
    t21 = $[36];
  }
  let t22;
  if ($[37] !== t19) {
    t22 = <motion.span className={t19} animate={t20} transition={t21} />;
    $[37] = t19;
    $[38] = t22;
  } else {
    t22 = $[38];
  }
  let t23;
  let t24;
  let t25;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = {
      scale: 0,
      rotate: -30
    };
    t24 = {
      scale: 1,
      rotate: 0
    };
    t25 = {
      type: "spring",
      stiffness: 300,
      damping: 18,
      delay: 0.1
    };
    $[39] = t23;
    $[40] = t24;
    $[41] = t25;
  } else {
    t23 = $[39];
    t24 = $[40];
    t25 = $[41];
  }
  const t26 = `relative grid size-16 place-items-center rounded-full border-2 border-black ${cancelled ? "bg-[#e5484d] text-white" : "bg-primary text-black"}`;
  let t27;
  if ($[42] !== cancelled) {
    t27 = cancelled ? <X size={28} strokeWidth={3} /> : <Check size={28} strokeWidth={3} />;
    $[42] = cancelled;
    $[43] = t27;
  } else {
    t27 = $[43];
  }
  let t28;
  if ($[44] !== t26 || $[45] !== t27) {
    t28 = <motion.span initial={t23} animate={t24} transition={t25} className={t26}>{t27}</motion.span>;
    $[44] = t26;
    $[45] = t27;
    $[46] = t28;
  } else {
    t28 = $[46];
  }
  let t29;
  if ($[47] !== t22 || $[48] !== t28) {
    t29 = <div className="relative grid size-16 place-items-center">{t22}{t28}</div>;
    $[47] = t22;
    $[48] = t28;
    $[49] = t29;
  } else {
    t29 = $[49];
  }
  let t30;
  if ($[50] !== cancelled || $[51] !== rescheduled) {
    t30 = rescheduled && !cancelled && <motion.span variants={modalRow} custom={0} initial="hidden" animate="visible" className="mt-5 rounded-full border border-primary/40 bg-primary/10 px-3 py-1 text-[10px] font-black uppercase tracking-[0.2em] text-primary">Rescheduled</motion.span>;
    $[50] = cancelled;
    $[51] = rescheduled;
    $[52] = t30;
  } else {
    t30 = $[52];
  }
  const t31 = `text-3xl font-black uppercase leading-[0.95] tracking-tighter text-white md:text-4xl ${rescheduled && !cancelled ? "mt-4" : "mt-6"}`;
  let t32;
  if ($[53] !== headline || $[54] !== t31) {
    t32 = <motion.h3 variants={modalRow} custom={0} initial="hidden" animate="visible" className={t31}>{headline}</motion.h3>;
    $[53] = headline;
    $[54] = t31;
    $[55] = t32;
  } else {
    t32 = $[55];
  }
  let t33;
  if ($[56] !== blurb) {
    t33 = <motion.p variants={modalRow} custom={1} initial="hidden" animate="visible" className="mt-3 max-w-sm text-sm text-white/60">{blurb}</motion.p>;
    $[56] = blurb;
    $[57] = t33;
  } else {
    t33 = $[57];
  }
  let t34;
  if ($[58] !== t29 || $[59] !== t30 || $[60] !== t32 || $[61] !== t33) {
    t34 = <div className="relative flex flex-col items-center text-center">{t29}{t30}{t32}{t33}</div>;
    $[58] = t29;
    $[59] = t30;
    $[60] = t32;
    $[61] = t33;
    $[62] = t34;
  } else {
    t34 = $[62];
  }
  let t35;
  if ($[63] !== booking.title || $[64] !== booking.uid || $[65] !== cancelled || $[66] !== duration || $[67] !== error || $[68] !== facts || $[69] !== handleCancel || $[70] !== onBookAgain || $[71] !== onDismiss || $[72] !== onReschedule || $[73] !== participants || $[74] !== pending || $[75] !== reason || $[76] !== start || $[77] !== view) {
    t35 = view === "cancelling" ? <motion.div variants={modalRow} custom={2} initial="hidden" animate="visible" className="relative mt-8"><label htmlFor="cancel-reason" className="text-[10px] font-semibold uppercase tracking-[0.2em] text-white/40">Reason for cancellation</label><textarea id="cancel-reason" rows={4} value={reason} onChange={e => setReason(e.target.value)} placeholder="Why are you cancelling?" className="mt-3 w-full resize-none rounded-2xl border border-white/10 bg-white/5 p-4 text-sm text-white outline-none transition-colors placeholder:text-white/25 focus:border-primary" /><p className="mt-2 text-[11px] text-white/35">Your reason is shared with everyone on the invite.</p>{error && <p className="mt-3 rounded-xl border border-[#e5484d]/40 bg-[#e5484d]/10 px-3 py-2 text-xs text-[#ff9b9e]">{error}</p>}<div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-end"><AppButton onClick={() => setView("confirmed")} variant="ghost" block={true} buttonClass="h-12" className="sm:w-40" disabled={pending}>Nevermind</AppButton><AppButton onClick={handleCancel} variant="danger" block={true} buttonClass="h-12" className="sm:w-48" disabled={pending} icon={<Trash2 size={16} />}>{pending ? "Cancelling\u2026" : "Cancel event"}</AppButton></div></motion.div> : cancelled ? <motion.div variants={modalRow} custom={2} initial="hidden" animate="visible" className="relative mt-8 flex flex-col gap-3 sm:flex-row">{onBookAgain && <AppButton onClick={onBookAgain} block={true} buttonClass="h-12" className="flex-1" shadowClass="-translate-x-1 translate-y-1">Book another time</AppButton>}<AppButton onClick={onDismiss} variant="ghost" block={true} buttonClass="h-12" className="flex-1" icon={<Undo2 size={16} />}>Back to site</AppButton></motion.div> : <>{participants.length > 0 && <motion.div variants={modalRow} custom={2} initial="hidden" animate="visible" className="relative mt-8 rounded-2xl border border-white/10 bg-white/5 p-4"><p className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-white/40"><Users size={12} /> Participants</p><div className="mt-3 grid gap-2 sm:grid-cols-2">{participants.map(_temp2)}</div></motion.div>}{facts.length > 0 && <div className="relative mt-3 grid gap-3 sm:grid-cols-2">{facts.map(_temp3)}</div>}<motion.div variants={modalRow} custom={3 + facts.length} initial="hidden" animate="visible" className="relative mt-6 grid gap-3 sm:grid-cols-3">{start && duration && <AppButton href={googleCalendarUrl({
          title: booking.title,
          start,
          duration,
          details: "Booked via octarnal.com"
        })} newTab={true} variant="secondary" block={true} buttonClass="h-12" icon={<CalendarPlus size={16} />}>Add to calendar</AppButton>}{booking.uid && <AppButton onClick={() => onReschedule(booking.uid)} variant="ghost" block={true} buttonClass="h-12" icon={<Settings2 size={16} />}>Reschedule</AppButton>}<AppButton onClick={onDismiss} variant="ghost" block={true} buttonClass="h-12" icon={<Undo2 size={16} />}>Back to site</AppButton></motion.div>{booking.uid && <motion.button variants={modalRow} custom={4 + facts.length} initial="hidden" animate="visible" type="button" onClick={() => setView("cancelling")} className="relative mt-4 w-full cursor-pointer text-center text-xs font-semibold uppercase tracking-wider text-white/40 transition-colors hover:text-[#e5484d]">Cancel appointment</motion.button>}</>;
    $[63] = booking.title;
    $[64] = booking.uid;
    $[65] = cancelled;
    $[66] = duration;
    $[67] = error;
    $[68] = facts;
    $[69] = handleCancel;
    $[70] = onBookAgain;
    $[71] = onDismiss;
    $[72] = onReschedule;
    $[73] = participants;
    $[74] = pending;
    $[75] = reason;
    $[76] = start;
    $[77] = view;
    $[78] = t35;
  } else {
    t35 = $[78];
  }
  let t36;
  if ($[79] !== t16 || $[80] !== t18 || $[81] !== t34 || $[82] !== t35) {
    t36 = <motion.div initial={t11} animate={t12} exit={t13} transition={t14} className="relative w-full max-w-lg overflow-hidden rounded-3xl border-2 border-black bg-accent-foreground p-5 sm:p-6 md:max-w-2xl md:p-8 lg:max-w-3xl lg:p-10">{t16}{t18}{t34}{t35}</motion.div>;
    $[79] = t16;
    $[80] = t18;
    $[81] = t34;
    $[82] = t35;
    $[83] = t36;
  } else {
    t36 = $[83];
  }
  return t36;
};
const BookingSuccessCard = t0 => {
  const $ = _c(9);
  const {
    booking,
    viewerZone,
    onDismiss,
    onReschedule,
    onBookAgain
  } = t0;
  const open = Boolean(booking);
  useModalOverlay(open, onDismiss);
  if (typeof document === "undefined") {
    return null;
  }
  let t1;
  if ($[0] !== booking || $[1] !== onBookAgain || $[2] !== onDismiss || $[3] !== onReschedule || $[4] !== open || $[5] !== viewerZone) {
    t1 = open && booking && <motion.div className="fixed inset-0 z-1100 overflow-y-auto overflow-x-hidden overscroll-contain" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.25
    }} role="dialog" aria-modal="true" aria-label={booking.rescheduled ? "Booking rescheduled" : "Booking confirmed"}><div className="fixed inset-0 cursor-pointer bg-black/70 backdrop-blur-sm" onClick={onDismiss} aria-hidden={true} /><div className="relative flex min-h-full w-full items-center justify-center p-4 py-8"><BookingPanel key={booking.uid ?? "booking"} booking={booking} viewerZone={viewerZone} onDismiss={onDismiss} onReschedule={onReschedule} onBookAgain={onBookAgain} /></div></motion.div>;
    $[0] = booking;
    $[1] = onBookAgain;
    $[2] = onDismiss;
    $[3] = onReschedule;
    $[4] = open;
    $[5] = viewerZone;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== t1) {
    t2 = createPortal(<AnimatePresence>{t1}</AnimatePresence>, document.body);
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
};
export default BookingSuccessCard;
function _temp(f) {
  return f.value;
}
function _temp2(p, i) {
  return <InfoTileCard key={`${p.name}-${p.role}-${i}`} index={i} className="p-2" leading={<span className={`grid size-8 place-items-center rounded-full text-[10px] font-black ${p.role === "host" ? "bg-primary text-black" : "border border-primary/40 text-primary"}`}>{initialsOf(p.name)}</span>} value={p.name} sub={p.email} trailing={<span className="rounded-full bg-primary/15 px-2 py-0.5 text-[9px] font-black uppercase tracking-wider text-primary">{ROLE_BADGE[p.role]}</span>} />;
}
function _temp3(f_0, i_0) {
  const Icon = f_0.icon;
  return <InfoTileCard key={f_0.label} index={3 + i_0} label={f_0.label} value={<>{f_0.value}{f_0.sub && <span className="text-white/40"> · {f_0.sub}</span>}</>} leading={<span className="grid size-9 place-items-center rounded-full bg-primary/15 text-primary"><Icon size={16} /></span>} />;
}